import { useEffect, useRef, useState } from "react";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { Users, Trash2 } from "lucide-react";

export function EventChip({ m, isAdmin, availableNames = [], colorClass, onOpen, onDelete }) {
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const longPressedRef = useRef(false);
  const btnRef = useRef(null);
  const contentRef = useRef(null);

  const showAvailable = !m.assigned_to && m.status !== "completed";
  const hasPopover = showAvailable || isAdmin;

  // Close the popover when tapping/clicking outside (needed on touch where
  // there is no pointer-leave to auto-dismiss a long-press popover).
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (contentRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("touchstart", handler, true);
    document.addEventListener("mousedown", handler, true);
    return () => {
      document.removeEventListener("touchstart", handler, true);
      document.removeEventListener("mousedown", handler, true);
    };
  }, [open]);

  const startPress = () => {
    if (!hasPopover) return;
    longPressedRef.current = false;
    timerRef.current = setTimeout(() => {
      longPressedRef.current = true;
      setOpen(true);
    }, 450);
  };
  const clearPress = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  };
  const handleClick = (e) => {
    if (longPressedRef.current) { e.preventDefault(); longPressedRef.current = false; return; }
    onOpen(m);
  };

  const chip = (
    <button
      ref={btnRef}
      data-testid={`event-${m.id}`}
      onClick={handleClick}
      onContextMenu={(e) => { if (hasPopover) e.preventDefault(); }}
      onTouchStart={startPress}
      onTouchEnd={clearPress}
      onTouchMove={clearPress}
      onTouchCancel={clearPress}
      style={{ WebkitTouchCallout: "none", WebkitUserSelect: "none", userSelect: "none" }}
      className={`w-full text-left text-[11px] leading-tight font-medium px-1.5 py-1 rounded-md transition-colors active:scale-95 select-none ${colorClass}`}
    >
      <span className="block truncate pointer-events-none">{m.park_name}</span>
      {m.assigned_to_name && (
        <span className="block truncate text-[10px] opacity-90 font-normal pointer-events-none">
          {m.assigned_to_name}
        </span>
      )}
    </button>
  );

  if (!hasPopover) return chip;

  return (
    <HoverCard open={open} onOpenChange={setOpen} openDelay={120} closeDelay={80}>
      <HoverCardTrigger asChild>{chip}</HoverCardTrigger>
      <HoverCardContent ref={contentRef} className="w-56" data-testid={`event-popover-${m.id}`}>
        {showAvailable && (
          <>
            <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5" /> Disponibles ce jour
            </p>
            {availableNames.length > 0 ? (
              <ul className="text-sm space-y-1 max-h-44 overflow-y-auto">
                {availableNames.map((n) => (
                  <li key={n} className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />{n}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">Personne n'est disponible.</p>
            )}
          </>
        )}
        {isAdmin && (
          <>
            {showAvailable && <div className="my-2 border-t border-border" />}
            <button
              data-testid={`delete-event-${m.id}`}
              onClick={() => { setOpen(false); onDelete(m); }}
              className="flex w-full items-center gap-2 px-2 py-1.5 rounded-md text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors"
            >
              <Trash2 className="h-4 w-4" /> Supprimer
            </button>
          </>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}
